import React from "react";
import { connect } from "react-redux";
import { Dropdown, Modal, Button } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import {
	FiMoreHorizontal,
	FiUpload,
	FiRefreshCw,
	FiCheck,
	FiX,
} from "react-icons/fi";
import { showModal, hideModal } from "actions/modalActions";
import DocumentsAdd from "components/Documents/DocumentAddModal";
import PdfBulkResultsTable from "components/Documents/PdfBulkResultsTable";
import { hasSubscriptionFeature } from "utils/utils";
import UpgradeModal from "components/Modal/UpgradeModal";

class DocumentsBulkList extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			documents: [], // Array vacío para simular que no hay documentos inicialmente
			uploadResults: null, // Para almacenar los resultados del envío masivo
			originalFiles: [], // Para almacenar los archivos originales con su peso
			showResults: false, // Para controlar si mostrar la tabla de resultados
			isPdfResults: false, // Para detectar si son resultados de PDF
			showUpgradeModal: false,
			upgradeFeature: "",
			showAtomicPdfStoppedModal: false,
		};
	}

	openUploadModal = () => {
		if (!hasSubscriptionFeature("MASS_SENDING_DOCUMENTS")) {
			this.setState({
				showUpgradeModal: true,
				upgradeFeature: this.props.t("documents.bulkUpload"),
			});
			return;
		}

		// Resetear resultados antes de abrir el modal
		this.setState({
			uploadResults: null,
			showResults: false,
			originalFiles: [],
			isPdfResults: false,
			showAtomicPdfStoppedModal: false,
		});
		this.props.showModal(
			{
				open: true,
				title: this.props.t("documents.bulkUpload"),
				style: { width: "750px" },
				content: (
					<DocumentsAdd
						selectedTab="bulk"
						handleClose={this.closeModal}
						onDocumentAdded={this.handleDocumentAdded}
						onFilesSelected={this.handleFilesSelected}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	};

	closeModal = () => {
		this.props.hideModal();
	};

	handleFilesSelected = (files) => {
		// Guardar los archivos originales con su información de peso
		const filesWithInfo = files.map((file) => ({
			file: file,
			name: file.name,
			size: this.formatFileSize(file.size),
		}));
		this.setState({ originalFiles: filesWithInfo });
	};

	formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	handleDocumentAdded = (results, options = {}) => {
		// Cerrar el modal primero
		this.closeModal();
		// Sin metadatos del envío masivo PDF, no mostrar el aviso de “nada cargado” (evita falsos positivos)
		const loadDespiteErrors =
			options.loadDespiteErrors !== undefined
				? options.loadDespiteErrors
				: true;

		// Resetear resultados antes de abrir el modal
		if (this.state.uploadResults?.length > 0) {
			this.setState({
				uploadResults: null,
				showResults: false,
				originalFiles: [],
				isPdfResults: false,
				showAtomicPdfStoppedModal: false,
			});
		}

		// Procesar los resultados
		if (results && Array.isArray(results)) {
			// Detectar si son resultados de PDF (cuando solo hay un archivo original y múltiples resultados)
			const isPdfResults =
				this.state.originalFiles.length === 1 && results.length > 1;
			const errorCount = results.filter((r) => r.status === "ERROR").length;
			const showAtomicPdfStoppedModal =
				isPdfResults && errorCount > 0 && !loadDespiteErrors;

			if (isPdfResults) {
				// Para PDF, usar los resultados tal como vienen
				this.setState({
					uploadResults: results,
					showResults: true,
					isPdfResults: true,
					showAtomicPdfStoppedModal,
				});
			} else {
				// Para múltiples archivos, combinar con información original
				const processedResults = results.map((result) => {
					const originalFile = this.state.originalFiles.find(
						(f) => f.name === result.file_name
					);
					return {
						...result,
						size: originalFile ? originalFile.size : "N/A",
					};
				});

				this.setState({
					uploadResults: processedResults,
					showResults: true,
					isPdfResults: false,
					showAtomicPdfStoppedModal: false,
				});
			}
		}
	};

	handleRetryFailed = () => {
		// Filtrar solo los archivos que fallaron
		const failedResults = this.state.uploadResults.filter(
			(result) => result.status === "ERROR"
		);
		const failedFiles = failedResults
			.map((result) => {
				const originalFile = this.state.originalFiles.find(
					(f) => f.name === result.file_name
				);
				return originalFile ? originalFile.file : null;
			})
			.filter((file) => file !== null);

		if (failedFiles.length > 0) {
			// Abrir el modal con los archivos fallidos
			this.props.showModal(
				{
					open: true,
					title: this.props.t("documents.bulk.retryFailedModal"),
					style: { width: "700px" },
					content: (
						<DocumentsAdd
							selectedTab="bulk"
							handleClose={this.closeModal}
							onDocumentAdded={this.handleDocumentAdded}
							onFilesSelected={this.handleFilesSelected}
							preSelectedFiles={failedFiles}
							forceUploadMode="multiple" // Forzar modo múltiples archivos
						/>
					),
					closeModal: this.closeModal,
				},
				"edit"
			);
		}
	};

	clearResults = () => {
		this.setState({
			uploadResults: null,
			showResults: false,
			originalFiles: [],
			isPdfResults: false,
			showAtomicPdfStoppedModal: false,
		});
	};

	closeAtomicPdfStoppedModal = () => {
		this.setState({ showAtomicPdfStoppedModal: false });
	};

	getActions = () => {
		return (
			<Dropdown>
				<Dropdown.Toggle variant="action">
					<FiMoreHorizontal />
				</Dropdown.Toggle>
				<Dropdown.Menu>
					<Dropdown.Item as="button">{this.props.t('common.download')}</Dropdown.Item>
					<Dropdown.Item as="button">{this.props.t('common.delete')}</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	renderResultsTable = () => {
		const { uploadResults } = this.state;
		const { t } = this.props;

		if (!uploadResults || !Array.isArray(uploadResults)) {
			return null;
		}

		const successCount = uploadResults.filter(
			(result) => result.status === "SUCCESS"
		).length;
		const errorCount = uploadResults.filter(
			(result) => result.status === "ERROR"
		).length;

		return (
			<div className="upload-results-container">
				<div className="results-header">
					<div className="results-summary">
						<h4>{t("documents.bulk.results")}</h4>
						<div>
							<span className="success">
								<FiCheck style={{ marginRight: "5px" }} />
								{t("documents.bulk.successCount", { count: successCount })}
							</span>
							<span className="error">
								<FiX style={{ marginRight: "5px" }} />
								{t("documents.bulk.errorCount", { count: errorCount })}
							</span>
						</div>
					</div>
					<div className="results-actions">
						{errorCount > 0 && (
							<button
								type="button"
								onClick={this.handleRetryFailed}
								className="btn btn-outline-danger"
								style={{ marginRight: "10px" }}
							>
								<FiRefreshCw style={{ marginRight: "5px" }} />
								{t("documents.bulk.retryFailed")}
							</button>
						)}
						<button
							type="button"
							onClick={this.clearResults}
							className="btn btn-outline-secondary"
						>
							{t("common.close")}
						</button>
					</div>
				</div>

				<table className="table table-documents table-zebra">
					<thead>
						<tr>
							<th>{t("documents.columns.document")}</th>
							<th>{t("documents.columns.size")}</th>
							<th>{t("documents.columns.status")}</th>
							<th>{t("documents.columns.description")}</th>
						</tr>
					</thead>
					<tbody>
						{uploadResults.map((result, index) => (
							<tr key={index}>
								<td>
									<span className="document-name">{result.file_name}</span>
								</td>
								<td>{result.size}</td>
								<td>
									{result.status === "SUCCESS" ? (
										<span className="status-success">
											<FiCheck style={{ marginRight: "5px" }} />
											{t("documents.success")}
										</span>
									) : (
										<span className="status-error">
											<FiX style={{ marginRight: "5px" }} />
											{t("common.error")}
										</span>
									)}
								</td>
								<td>
									{result.error_description === "store_folder.employee_not_found"
										? t("documents.employeeNotFound")
										: result.error_description || "-"}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	};

	renderEmptyState = () => {
		return (
			<div className="empty-state-container">
				<div className="empty-state-icon">
					<FiUpload size={40} />
				</div>
				<h3>{this.props.t("documents.bulk.noDocumentsSent")}</h3>
				<p>{this.props.t("documents.bulk.startUploadingBulk")}</p>
				{(() => {
					const hasMassSendingSubscription = hasSubscriptionFeature(
						"MASS_SENDING_DOCUMENTS"
					);
					return (
						<button
							type="button"
							onClick={this.openUploadModal}
							className="btn btn-primary"
							style={{
								opacity: !hasMassSendingSubscription ? 0.5 : 1,
								cursor: !hasMassSendingSubscription ? "not-allowed" : "pointer",
							}}
						>
							<FiUpload style={{ marginRight: "8px" }} />
							{this.props.t("documents.uploadDocuments")}
						</button>
					);
				})()}
			</div>
		);
	};

	renderTable = () => {
		const { t } = this.props;
		return (
			<table className="table table-documents table-zebra">
				<thead>
					<tr>
						<th>{t("documents.columns.document")}</th>
						<th>{t("documents.columns.size")}</th>
						<th>{t("documents.columns.sent")}</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr key="1">
						<td>
							<button className="btn-transparent btn-editable document-name">
								49648876Z_nomina_04.pdf
							</button>
						</td>
						<td>0,25Mb</td>
						<td>30/04/2021</td>
						<td className="td-actions">{this.getActions()}</td>
					</tr>
					<tr key="2">
						<td>
							<button className="btn-transparent btn-editable document-name">
								37451125M_nomina_04.pdf
							</button>
						</td>
						<td>0,25Mb</td>
						<td>30/04/2021</td>
						<td className="td-actions">{this.getActions()}</td>
					</tr>
					<tr key="3">
						<td>
							<button className="btn-transparent btn-editable document-name">
								49648876Z_nomina_03.pdf
							</button>
						</td>
						<td>0,26Mb</td>
						<td>31/03/2021</td>
						<td className="td-actions">{this.getActions()}</td>
					</tr>
					<tr key="4">
						<td>
							<button className="btn-transparent btn-editable document-name">
								37451125M_nomina_03.pdf
							</button>
						</td>
						<td>0,26Mb</td>
						<td>31/03/2021</td>
						<td className="td-actions">{this.getActions()}</td>
					</tr>
					<tr key="5">
						<td>
							<button className="btn-transparent btn-editable document-name">
								49648876Z_nomina_02.pdf
							</button>
						</td>
						<td>0,25Mb</td>
						<td>28/02/2021</td>
						<td className="td-actions">{this.getActions()}</td>
					</tr>
					<tr key="6">
						<td>
							<button className="btn-transparent btn-editable document-name">
								37451125M_nomina_02.pdf
							</button>
						</td>
						<td>0,25Mb</td>
						<td>28/02/2021</td>
						<td className="td-actions">{this.getActions()}</td>
					</tr>
					<tr key="7">
						<td>
							<button className="btn-transparent btn-editable document-name">
								49648876Z_contratos.zip
							</button>
						</td>
						<td>0,25Mb</td>
						<td>01/02/2021</td>
						<td className="td-actions">{this.getActions()}</td>
					</tr>
				</tbody>
			</table>
		);
	};

	render() {
		const { documents, showResults, isPdfResults, uploadResults } = this.state;

		// Si hay resultados de envío masivo, mostrar la tabla de resultados
		if (showResults) {
			// Si son resultados de PDF, mostrar el componente específico
			if (isPdfResults) {
				const pdfSuccessCount = Array.isArray(uploadResults)
					? uploadResults.filter((r) => r.status === "SUCCESS").length
					: 0;
				const showScannedPdfHint = pdfSuccessCount === 0;

				return (
					<>
						<PdfBulkResultsTable
							results={uploadResults}
							onClearResults={this.clearResults}
						/>
						<Modal
							show={this.state.showAtomicPdfStoppedModal}
							onHide={this.closeAtomicPdfStoppedModal}
							centered
							dialogClassName="atomic-pdf-stopped-modal-dialog"
						>
							<Modal.Header closeButton>
								<Modal.Title>{this.props.t("documents.bulk.noPayrollsLoaded")}</Modal.Title>
							</Modal.Header>
							<Modal.Body>
								<p>{this.props.t("documents.bulk.atomicPdfStoppedText1")}</p>
								<p>{this.props.t("documents.bulk.atomicPdfStoppedText2")}</p>
								<p className={showScannedPdfHint ? "" : "mb-0"}>
									{this.props.t("documents.bulk.atomicPdfStoppedText3")}
								</p>
								{showScannedPdfHint && (
									<div className="alert alert-warning mt-3 mb-0">
										{this.props.t("documents.bulk.scannedPdfHint")}
									</div>
								)}
							</Modal.Body>
							<Modal.Footer>
								<Button
									variant="primary"
									onClick={this.closeAtomicPdfStoppedModal}
								>
									{this.props.t("documents.understood")}
								</Button>
							</Modal.Footer>
						</Modal>
						<UpgradeModal
							show={this.state.showUpgradeModal}
							onClose={() => this.setState({ showUpgradeModal: false })}
							feature={this.state.upgradeFeature}
							requiredPlan="Pro"
						/>
					</>
				);
			}
			// Si no, mostrar la tabla normal
			return (
				<>
					{this.renderResultsTable()}
					<UpgradeModal
						show={this.state.showUpgradeModal}
						onClose={() => this.setState({ showUpgradeModal: false })}
						feature={this.state.upgradeFeature}
						requiredPlan="Pro"
					/>
				</>
			);
		}

		// Si no hay documentos, mostrar el estado vacío con el botón
		if (!documents || documents.length === 0) {
			return (
				<>
					{this.renderEmptyState()}
					<UpgradeModal
						show={this.state.showUpgradeModal}
						onClose={() => this.setState({ showUpgradeModal: false })}
						feature={this.state.upgradeFeature}
						requiredPlan="Pro"
					/>
				</>
			);
		}

		// Si hay documentos, mostrar la tabla
		return (
			<>
				{this.renderTable()}
				<UpgradeModal
					show={this.state.showUpgradeModal}
					onClose={() => this.setState({ showUpgradeModal: false })}
					feature={this.state.upgradeFeature}
					requiredPlan="Pro"
				/>
			</>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		// ...reducers.shiftsReducer,
		// ...reducers.workflowsReducer,
		user: reducers.authReducer.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(DocumentsBulkList));
